import axios from 'axios';
import { Config } from '../config/config';
import { ISession } from "../model/session.model";

class SessionService {
    private session: ISession;
    private validationPromise: Promise<ISession> | null = null;
    private lastValidationTime: number = 0;
    private readonly VALIDATION_CACHE_TIME = 5000; // 5 seconds cache
    private sessionListeners: Array<(session: ISession) => void> = [];

    constructor() {
        const storedToken = localStorage.getItem('token') || '';
        const storedLoggedIn = localStorage.getItem('loggedIn') === 'true';
        
        console.log('[DEBUG] SessionService: Konstruktor', { 
            storedToken: storedToken ? `${storedToken.substring(0, 8)}...` : 'none',
            storedLoggedIn
        });
        
        this.session = {
            token: storedToken,
            loggedIn: storedLoggedIn,
        };
        
        // Setze den Authorization-Header, wenn ein Token existiert
        if (storedToken) {
            axios.defaults.headers.common = { Authorization: `Bearer ${storedToken}` };
            console.log('[DEBUG] SessionService: Authorization-Header gesetzt');
        }
    }

    public async validate(): Promise<ISession> {
        console.log('[DEBUG] validate()-Methode aufgerufen');
        
        // Wenn kein Token vorhanden ist, direkt zurückgeben ohne API-Aufruf
        if (!this.session?.token) {
            console.log('[DEBUG] Kein Token vorhanden, keine Validierung durchgeführt');
            this.session.loggedIn = false;
            return this.session;
        }

        // Prüfen, ob eine Validierung bereits im Gange ist
        if (this.validationPromise) {
            console.log('[DEBUG] Validierung läuft bereits, nutze vorhandene Promise');
            return this.validationPromise;
        }

        // Prüfen, ob das Ergebnis aus dem Cache genutzt werden kann
        const now = Date.now();
        if (now - this.lastValidationTime < this.VALIDATION_CACHE_TIME) {
            console.log('[DEBUG] Nutze gecachtes Validierungsergebnis');
            return this.session;
        }
        
        try {
            // Setze Promise für laufende Validierung
            this.validationPromise = this._executeValidation();
            const result = await this.validationPromise;
            return result;
        } finally {
            // Zurücksetzen der Promise nach Abschluss der Validierung
            this.validationPromise = null;
        }
    }

    private async _executeValidation(): Promise<ISession> {
        try {
            console.log('[DEBUG] Sende validate-Anfrage mit Token:', this.session?.token);
            const response = await axios.get(
                'api/session/validate',
                { headers: { Authorization: `Bearer ${this.session?.token}` } }
            );

            console.log('[DEBUG] validate-Antwort erhalten:', response.data);
            const session = response.data as ISession;
            this.setSession(session);
            this.lastValidationTime = Date.now();
            return this.session;
        } catch (error) {
            console.error('[DEBUG] API-Fehler bei Session-Validierung:', error);
            this.session.loggedIn = false;
            this.persistLoggedIn(false);
            this.fireEvent(this.session);
            return this.session;
        }
    }

    setSession(session: ISession) {
        this.session = session;
        this.persistToken(session.token);
        this.persistLoggedIn(session.loggedIn ?? false);;
        this.fireEvent(this.session);
    }
    

    getSessionDetails(): ISession {
        return this.session;
    }

    private persistToken(token: string) {
        console.log('[DEBUG] SessionService: Speichere Token', 
            token ? `${token.substring(0, 8)}...` : 'none');
        
        if (token) {
            axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
            localStorage.setItem('token', token);
        } else {
            delete axios.defaults.headers.common['Authorization'];
            localStorage.removeItem('token');
        }
    }

    getToken(): string {
        return this.session?.token;
    }

    private persistLoggedIn(loggedIn: boolean) {
        localStorage.setItem('loggedIn', loggedIn.toString());
    }

    getLoggedIn(): boolean {
        return this.session.loggedIn;
    }

    async signOut(): Promise<void> {
        // Token und loggedIn-Status vollständig löschen
        console.log('[DEBUG] SessionService: SignOut - Token wird entfernt');
        localStorage.removeItem('token');
        localStorage.removeItem('loggedIn');
        
        // Authorization-Header entfernen
        delete axios.defaults.headers.common['Authorization'];
        
        const session: ISession = {
            token: '',
            loggedIn: false,
        };

        this.session = session;
        this.fireEvent(session);
        
        console.log('[DEBUG] SessionService: SignOut abgeschlossen');
    }

    private fireEvent(session: ISession) {
        this.sessionListeners.forEach(listener => {
            try {
                listener(session);
            } catch (e) {
                console.error('Fehler im Session-Listener:', e);
            }
        });
    }

    // Methode zum Initialisieren einer neuen Session, wenn keine existiert
    public async initializeSession(): Promise<ISession> {
        console.log('[DEBUG] SessionService: Initialisiere neue Session');
        
        // Wenn bereits ein Token existiert, validiere diesen
        if (this.session?.token) {
            console.log('[DEBUG] SessionService: Token existiert bereits, validiere Session');
            return this.validate();
        }
        
        try {
            // Anfrage an das Backend, um eine neue Session zu erstellen
            console.log('[DEBUG] SessionService: Fordere neue Session vom Server an');
            const response = await axios.get('api/session');
            
            console.log('[DEBUG] SessionService: Neue Session erhalten:', response.data);
            const session = response.data as ISession;
            this.setSession(session);
            return this.session;
        } catch (error) {
            console.error('[DEBUG] SessionService: Fehler beim Erstellen einer neuen Session:', error);
            
            // Erstelle eine lokale Session mit leerem Token
            const emptySession: ISession = {
                token: '',
                loggedIn: false
            };
            
            this.session = emptySession;
            return this.session;
        }
    }

    /**
     * Fügt einen Listener für Session-Änderungen hinzu
     */
    public addSessionListener(listener: (session: ISession) => void): void {
        this.sessionListeners.push(listener);
    }

    /**
     * Entfernt einen Listener für Session-Änderungen
     */
    public removeSessionListener(listener: (session: ISession) => void): void {
        this.sessionListeners = this.sessionListeners.filter(l => l !== listener);
    }

    /**
     * Gibt die aktuelle Session zurück ohne Validierung
     */
    public getCurrentSession(): ISession | null {
        return this.session || null;
    }

    /**
     * Gibt zurück, ob der aktuelle Benutzer Admin-Rechte hat
     */
    public async isAdmin(): Promise<boolean> {
        try {
            // Wenn nicht eingeloggt, kann der Benutzer kein Admin sein
            if (!this.session?.loggedIn) {
                return false;
            }
            
            // Sende Anfrage an den is-admin Endpoint
            console.log('[DEBUG] SessionService: Sende Admin-Status-Anfrage');
            const response = await axios.get(
                'api/admin/is-admin',
                { headers: { Authorization: `Bearer ${this.session?.token}` } }
            );
            
            console.log('[DEBUG] SessionService: Admin-Status-Antwort erhalten:', response.data);
            
            // Extrahiere Admin-Status aus der Antwort
            return response.data.isAdmin || false;
        } catch (error) {
            console.error('[DEBUG] SessionService: Fehler beim Prüfen des Admin-Status', error);
            return false;
        }
    }
}

export const sessionService = new SessionService();
